import { RootStateOrAny } from 'react-redux';

/**
 * Interface for RAM Assistant feature flag configuration
 */
interface RamAssistantFeatureFlags {
  /** Enable RAM Assistant for all users regardless of other conditions */
  enableForAll?: boolean;
  /** List of restaurant IDs that should have access to RAM Assistant */
  allowedRestaurantIds?: number[];
  /**
   * List of user brand, brand-region, or brand-region-countryId combinations that should have access
   * Supports three formats:
   * - "BRAND" (e.g., "BK", "TH") - matches user's brand only
   * - "BRAND-REGION" (e.g., "TH-NA", "BK-EMEA") - matches user's brand and region
   * - "BRAND-REGION-COUNTRYID" (e.g., "BK-NA-1", "TH-EMEA-2") - matches user's brand, region, and countryId (integer)
   * Note: User countryId comes from FRUM.countries
   */
  allowedUserBrandsRegions?: string[];
  /**
   * List of restaurant brand, brand-region, or brand-region-countryId combinations that should have access
   * Supports three formats:
   * - "BRAND" (e.g., "BK", "TH") - matches restaurant's brand only
   * - "BRAND-REGION" (e.g., "TH-NA", "BK-EMEA") - matches restaurant's brand and region
   * - "BRAND-REGION-COUNTRYID" (e.g., "BK-NA-1", "TH-EMEA-2") - matches restaurant's brand, region, and countryId (integer)
   * Note: Restaurant countryId comes from mds_repl.EBX_db_ref_country
   */
  allowedRestaurantBrandsRegions?: string[];
  /** List of franchise groups that should have access to RAM Assistant */
  allowedFranchiseGroups?: number[];
  /** List of usernames that should have access to RAM Assistant */
  allowedUsernames?: string[];
}

/**
 * Checks if RAM Assistant should be enabled based on feature flags and current state
 * Uses OR logic - if any condition is met, RAM Assistant will be enabled
 *
 * @param state - Redux state
 * @param flags - LaunchDarkly feature flags
 * @returns boolean indicating if RAM Assistant should be enabled
 */
export function shouldEnableRamAssistant(
  state: RootStateOrAny,
  flags: RamAssistantFeatureFlags,
): boolean {
  // If no flags are configured, disable RAM Assistant
  if (!flags || Object.keys(flags).length === 0) {
    return false;
  }

  const { RestaurantId, Region, CountryId, FranchiseGroupID } =
    state.RestaurantsReducer?.entity || {};

  const selectedBrand = state.UIReducer?.selectedBrand;
  const username = state.UserReducer?.userInfo?.sAMAccountName;
  const userBrandCodes: string[] =
    state.UserReducer?.userInfo?.userBrandCodes || [];
  const userRegion = state.UserReducer?.userInfo?.userRegion;
  const userCountryId = state.UserReducer?.userInfo?.countryId;

  // Check if enableForAll is true first (overrides all other conditions)
  if (flags.enableForAll === true) {
    return true;
  }

  // Check each condition with OR logic
  const conditions = [
    // 1. Check if selected restaurant is in allowed restaurants list
    checkRestaurantAccess(RestaurantId, flags.allowedRestaurantIds),

    // 2. Check if any of user's brand(-region)(-countryId) combinations is in allowed list
    checkUserBrandsRegionAccess({
      userBrandCodes,
      userRegion,
      userCountryId,
      allowedBrandsRegions: flags.allowedUserBrandsRegions,
    }),

    // 3. Check if selected restaurant's brand(-region)(-countryId) combination is in allowed list
    checkBrandRegionAccess({
      selectedBrand,
      region: Region,
      countryId: CountryId,
      allowedBrandsRegions: flags.allowedRestaurantBrandsRegions,
    }),

    // 4. Check if selected restaurant's franchise group is in allowed franchise groups list
    checkFranchiseGroupAccess(FranchiseGroupID, flags.allowedFranchiseGroups),

    // 5. Check if logged in username is in allowed usernames list
    checkUsernameAccess(username, flags.allowedUsernames),
  ];

  // Return true if any condition is met (OR logic)
  return conditions.some(condition => condition === true);
}

/**
 * Checks if the selected restaurant is in the allowed restaurants list
 */
function checkRestaurantAccess(
  restaurantId: number | undefined,
  allowedRestaurantIds?: number[],
): boolean {
  if (
    !allowedRestaurantIds ||
    allowedRestaurantIds.length === 0 ||
    !restaurantId
  ) {
    return false;
  }

  return allowedRestaurantIds.includes(restaurantId);
}

/**
 * Checks if any of the user's brand(-region)(-country) combinations is in the allowed list
 * Supports three formats:
 * - "BRAND" (e.g., "BK", "TH") - matches brand only
 * - "BRAND-REGION" (e.g., "TH-NA", "BK-EMEA") - matches brand and region
 * - "BRAND-REGION-COUNTRYID" (e.g., "BK-NA-1", "TH-EMEA-2") - matches brand, region, and countryId
 */
function checkUserBrandsRegionAccess({
  userBrandCodes,
  userRegion,
  userCountryId,
  allowedBrandsRegions,
}: {
  userBrandCodes: string[];
  userRegion: string | undefined;
  userCountryId: number | undefined;
  allowedBrandsRegions?: string[];
}): boolean {
  if (
    !allowedBrandsRegions ||
    allowedBrandsRegions.length === 0 ||
    !userBrandCodes ||
    userBrandCodes.length === 0
  ) {
    return false;
  }

  // Check if any of the user's brands (and optionally region/countryId) is allowed
  return userBrandCodes.some(brandCode => {
    const normalizedBrand = brandCode.toUpperCase();
    const brandRegion = userRegion
      ? `${brandCode}-${userRegion}`.toUpperCase()
      : null;
    const brandRegionCountry =
      userRegion && userCountryId !== undefined && userCountryId !== null
        ? `${brandCode}-${userRegion}-${userCountryId}`.toUpperCase()
        : null;

    return allowedBrandsRegions.some(allowed => {
      const normalizedAllowed = allowed.toUpperCase();
      const parts = normalizedAllowed.split('-');

      if (parts.length === 1) {
        // Format: BRAND - match brand only
        return normalizedAllowed === normalizedBrand;
      }
      if (parts.length === 2) {
        // Format: BRAND-REGION - match brand and region
        return brandRegion && normalizedAllowed === brandRegion;
      }
      if (parts.length === 3) {
        // Format: BRAND-REGION-COUNTRYID - match brand, region, and countryId
        return brandRegionCountry && normalizedAllowed === brandRegionCountry;
      }
      return false;
    });
  });
}

/**
 * Checks if the selected brand(-region)(-country) combination is in the allowed list
 * Supports three formats:
 * - "BRAND" (e.g., "BK", "TH") - matches brand only
 * - "BRAND-REGION" (e.g., "TH-NA", "BK-EMEA") - matches brand and region
 * - "BRAND-REGION-COUNTRYID" (e.g., "BK-NA-1", "TH-EMEA-2") - matches brand, region, and countryId
 */
function checkBrandRegionAccess({
  selectedBrand,
  region,
  countryId,
  allowedBrandsRegions,
}: {
  selectedBrand: string | undefined;
  region: string | undefined;
  countryId: number | undefined;
  allowedBrandsRegions?: string[];
}): boolean {
  if (
    !allowedBrandsRegions ||
    allowedBrandsRegions.length === 0 ||
    !selectedBrand
  ) {
    return false;
  }

  const normalizedBrand = selectedBrand.toUpperCase();
  const brandRegion = region
    ? `${selectedBrand}-${region}`.toUpperCase()
    : null;
  const brandRegionCountry =
    region && countryId !== undefined && countryId !== null
      ? `${selectedBrand}-${region}-${countryId}`.toUpperCase()
      : null;

  return allowedBrandsRegions.some(allowed => {
    const normalizedAllowed = allowed.toUpperCase();
    const parts = normalizedAllowed.split('-');

    if (parts.length === 1) {
      // Format: BRAND - match brand only
      return normalizedAllowed === normalizedBrand;
    }
    if (parts.length === 2) {
      // Format: BRAND-REGION - match brand and region
      return brandRegion && normalizedAllowed === brandRegion;
    }
    if (parts.length === 3) {
      // Format: BRAND-REGION-COUNTRYID - match brand, region, and countryId
      return brandRegionCountry && normalizedAllowed === brandRegionCountry;
    }
    return false;
  });
}

/**
 * Checks if the selected restaurant's franchise group is in the allowed franchise groups list
 */
function checkFranchiseGroupAccess(
  franchiseGroup: number | undefined,
  allowedFranchiseGroups?: number[],
): boolean {
  if (
    !allowedFranchiseGroups ||
    allowedFranchiseGroups.length === 0 ||
    !franchiseGroup
  ) {
    return false;
  }

  return allowedFranchiseGroups.includes(franchiseGroup);
}

/**
 * Checks if the logged in username is in the allowed usernames list
 */
function checkUsernameAccess(
  username: string | undefined,
  allowedUsernames?: string[],
): boolean {
  if (!allowedUsernames || allowedUsernames.length === 0 || !username) {
    return false;
  }

  // Convert username to uppercase for case-insensitive comparison
  const normalizedUsername = username.toUpperCase();
  return allowedUsernames.some(
    allowedUsername => allowedUsername.toUpperCase() === normalizedUsername,
  );
}
